import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as action from '../../store/actions';
import TableContainer from "../../components/Common/TableContainer";
import TransactionModal from "./TransactionModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ar } from 'date-fns/locale';
import { format, parseISO, isWithinInterval, subDays } from 'date-fns';
import { Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, Badge, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import * as XLSX from 'xlsx';
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";



function FinancialReport() {
  const transactions = useSelector(state => state.transactions.transactions);
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenDetails, setIsModalOpenDetails] = useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedTransactionDetails, setSelectedTransactionDetails] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [quickFilter, setQuickFilter] = useState('الكل');


  useEffect(() => {
    dispatch(action.fetchTransactions());
  }, [dispatch]);

  const formatTransactionDate = (date) => {
    if (!date) return 'غير متوفر';
    
    try {
      let parsedDate;
      
      if (typeof date === 'object' && 'seconds' in date) {
        parsedDate = new Date(date.seconds * 1000);
      } 
      else if (typeof date === 'string') {
        parsedDate = parseISO(date);
      }
      else if (date instanceof Date) {
        parsedDate = date;
      } else {
        return 'تاريخ غير صالح';
      }
      
      return format(parsedDate, 'yyyy/MM/dd HH:mm', { locale: ar });
    } catch (error) {
      console.error('خطأ في تحويل التاريخ:', error);
      return 'تاريخ غير صالح';
    }
  };

  const toggleModalDetails = () => {
    setIsModalOpenDetails(!isModalOpenDetails);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleDateRangeModal = () => {
    setIsDateRangeModalOpen(!isDateRangeModalOpen);
  };

  const handleRowClickDetails = transaction => {
    setSelectedTransactionDetails(transaction);
    toggleModalDetails();
  };

  const handleRowClick = transaction => {
    setSelectedTransaction(transaction);
    toggleModal();
  };

  const applyQuickFilter = (range) => {
    setQuickFilter(range);
    let start = null;
    let end = new Date();
    
    switch(range) {
      case 'اليوم':
        start = new Date();
        start.setHours(0, 0, 0, 0);
        break;
      case 'الأسبوع':
        start = subDays(new Date(), 7);
        break;
      case 'الشهر':
        start = subDays(new Date(), 30);
        break;
      default:
        end = null;
        break;
    }
    
    setDateRange([start, end]);
  };

  const applyDateFilter = () => {
    toggleDateRangeModal();
  };

  const clearFilters = () => {
    setDateRange([null, null]);
    setQuickFilter('الكل');
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      // Filter for pending payments only
      if (transaction.payment_status !== 'pending') return false;
      
      // Date range filtering if set
      if (startDate && endDate) {
        try {
          let transactionDate;
          
          if (transaction.created_at?.seconds) {
            transactionDate = new Date(transaction.created_at.seconds * 1000);
          } else if (transaction.created_at) {
            transactionDate = parseISO(transaction.created_at);
          } else {
            return false;
          }
          
          return isWithinInterval(transactionDate, {
            start: startDate,
            end: endDate
          });
        } catch (error) {
          console.error('خطأ في تصفية التاريخ:', error);
          return false;
        }
      }
      
      return true;
    });
  }, [transactions, startDate, endDate]);



 // Add a dropdown state for export options
 const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
 const toggleExportDropdown = () => setExportDropdownOpen(prevState => !prevState);

 // Function to export as Excel
 const exportToExcel = () => {
   const worksheet = XLSX.utils.json_to_sheet(filteredTransactions);
   const workbook = XLSX.utils.book_new();
   XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
   XLSX.writeFile(workbook, "transactions.xlsx");
 };

 // Function to export as PDF
 const exportToPDF = () => {
  // Initialize PDF document
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  // Set document metadata
  doc.setProperties({
    title: 'التقارير المالية',
    subject: 'كشف الحسابات',
    author: 'Your Application Name',
    keywords: 'تقارير, مالية, كشف حساب',
    creator: 'Your Application Name'
  });

  // Add title
  doc.setFontSize(20);
  doc.setTextColor(0, 0, 0); // Black text
  doc.text('التقارير المالية', 148.5, 20, { align: 'center' });

  // Add date range if filtered
  if (startDate || endDate) {
    doc.setFontSize(12);
    doc.text(
      `الفترة من ${startDate ? format(startDate, 'yyyy/MM/dd', { locale: ar }) : 'بداية'} إلى ${endDate ? format(endDate, 'yyyy/MM/dd', { locale: ar }) : 'نهاية'}`,
      148.5,
      30,
      { align: 'center' }
    );
  }

  // Prepare table data
  const headers = [
    { content: 'رقم العملية', styles: { halign: 'center', fillColor: [22, 160, 133] } },
    { content: 'تاريخ العملية', styles: { halign: 'center', fillColor: [22, 160, 133] } },
    { content: 'مقدم الخدمة', styles: { halign: 'center', fillColor: [22, 160, 133] } },
    { content: 'المبلغ', styles: { halign: 'center', fillColor: [22, 160, 133] } },
    { content: 'حالة الدفع', styles: { halign: 'center', fillColor: [22, 160, 133] } }
  ];

  const data = filteredTransactions.map((transaction, index) => [
    { content: index + 1, styles: { halign: 'center' } },
    { content: formatTransactionDate(transaction.created_at), styles: { halign: 'center' } },
    { content: transaction.serviceProviders?.salon_name || 'غير متوفر', styles: { halign: 'right' } },
    { content: `${transaction.total} ر.س`, styles: { halign: 'left' } },
    { 
      content: transaction.payment_status === 'pending' ? 'قيد الانتظار' : 'تم الدفع',
      styles: { 
        halign: 'center',
        fillColor: transaction.payment_status === 'pending' ? [243, 156, 18] : [39, 174, 96],
        textColor: [255, 255, 255]
      }
    }
  ]);

  // Generate table
  autoTable(doc, {
    head: [headers],
    body: data,
    startY: 40,
    styles: {
      font: 'helvetica',
      fontSize: 10,
      textColor: [0, 0, 0],
      cellPadding: 5
    },
    headStyles: {
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 30 },
      2: { cellWidth: 50 },
      3: { cellWidth: 25 },
      4: { cellWidth: 30 }
    },
    margin: { right: 10, left: 10 },
    didDrawPage: function (data) {
      // Footer
      doc.setFontSize(10);
      doc.setTextColor(150);
      doc.text(
        `الصفحة ${data.pageCount}`,
        data.settings.margin.left,
        doc.internal.pageSize.height - 10
      );
    }
  });

  // Save PDF with Arabic filename
  doc.save(`التقرير_المالي_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
};
 // Function to export single transaction as PDF (invoice)
 const exportSingleToPDF = (transaction) => {
   if (!transaction) return;
   
   const doc = new jsPDF();
   
   // Add title
   doc.setFontSize(18);
   doc.text("فاتورة", 105, 15, { align: 'center' });
   
   // Add transaction details
   doc.setFontSize(12);
   doc.text(`رقم الفاتورة: ${transaction.id || 'غير متوفر'}`, 14, 30);
   doc.text(`التاريخ: ${formatTransactionDate(transaction.created_at)}`, 14, 40);
   
   // Add provider info
   doc.text(`مقدم الخدمة: ${transaction.serviceProviders?.salon_name || 'غير متوفر'}`, 14, 50);
   doc.text(`الهاتف: ${transaction.serviceProviders?.mobile || 'غير متوفر'}`, 14, 60);
   
   // Add amount
   doc.setFontSize(14);
   doc.text(`المبلغ: ${transaction.total} ر.س`, 14, 75);
   
   // Add payment status
   doc.text(`حالة الدفع: ${transaction.payment_status === 'pending' ? 'قيد الانتظار' : transaction.payment_status}`, 14, 85);
   
   // Add footer
   doc.setFontSize(10);
   doc.text("شكراً لاستخدامكم منصتنا", 105, 150, { align: 'center' });
   
   doc.save(`invoice_${transaction.id}.pdf`);
 };

 const exportSingleToExcel = (transaction) => {
  if (!transaction) return;

  // Prepare the data for Excel
  const worksheetData = [
    ["نوع البيان", "القيمة"],
    ["رقم الفاتورة", transaction.id || 'غير متوفر'],
    ["تاريخ العملية", formatTransactionDate(transaction.created_at)],
    ["مقدم الخدمة", transaction.serviceProviders?.salon_name || 'غير متوفر'],
    ["هاتف مقدم الخدمة", transaction.serviceProviders?.mobile || 'غير متوفر'],
    ["القيمة بعد الخصم", `${Number(transaction.total_after_discount || transaction.amount || 0).toFixed(2)} ر.س`],
    ["غرامات الإلغاء", `${Number(transaction.fine_deductions_total || 0).toFixed(2)} ر.س`],
    ["الصافي بعد الغرامات", `${Number(transaction.net_after_fines || 0).toFixed(2)} ر.س`],
    ["سبب الغرامة", transaction.fine_last_reason || '-'],
    ["حالة الدفع", transaction.payment_status === 'pending' ? 'قيد الانتظار' : 'تم الدفع'],
    ["تاريخ التنفيذ", transaction.booking_execute_date ? formatTransactionDate(transaction.booking_execute_date) : 'غير محدد'],
    ["عدد المستفيدين", transaction.The_number_of_beneficiaries || 'غير محدد'],
    ["ملاحظات", transaction.comment || 'لا يوجد']
  ];

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "فاتورة");

  // Style the header row
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "1686D4" } },
    alignment: { horizontal: "center" }
  };

  // Apply styles (using xlsx-js-style or similar library if needed)
  // Note: For advanced styling, you might need xlsx-js-style library

  // Export the file
  XLSX.writeFile(workbook, `فاتورة_${transaction.id}.xlsx`);
};


  const columns = useMemo(
    () => [
      {
        Header: "تاريخ العملية",
        accessor: "created_at",
        Cell: ({ value }) => formatTransactionDate(value),
      },
      {
        Header: "الخدمة (صافي)",
        accessor: "total_after_discount",
        Cell: ({ value }) => (value ? `${Number(value).toFixed(2)} ر.س` : 'غير متوفر'),
      },
      {
        Header: "خيارات التصدير",
        accessor: "exports",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            {/* <Button
              color="primary"
              size="sm"
              onClick={() => exportSingleToPDF(row.original)}
              className="py-1 px-2"
            >
              <i className="mdi mdi-file-pdf-outline me-1" />
              PDF
            </Button> */}
            <Button
              color="success"
              size="sm"
              onClick={() => exportSingleToExcel(row.original)}
              className="py-1 px-2"
            >
              <i className="mdi mdi-file-excel-outline me-1" />
              Excel
            </Button>
          </div>
        ),
      },
      {
        Header: "مقدم الخدمة",
        accessor: "serviceProviders.salon_name",
        Cell: ({ value }) => value || 'غير متوفر',
      },
      {
        Header: "غرامات الإلغاء",
        accessor: "fine_deductions_total",
        Cell: ({ value }) => (
          <span className="text-danger">{Number(value || 0).toFixed(2)} ر.س</span>
        ),
      },
      {
        Header: "الصافي بعد الغرامات",
        accessor: "net_after_fines",
        Cell: ({ value }) => (
          <span className="text-primary">{Number(value || 0).toFixed(2)} ر.س</span>
        ),
      },
      {
        Header: "سبب الغرامة",
        accessor: "fine_last_reason",
        Cell: ({ value }) => value || '-',
      },
      // {
      //   Header: "المبلغ",
      //   accessor: "total",
      //   Cell: ({ value }) => `${value} ر.س`,
      // },
      {
        Header: "حالة الدفع",
        accessor: "payment_status",
        Cell: ({ value }) => <Status value={value} />,
      },
      {
        Header: "عرض التفاصيل",
        accessor: "details",
        Cell: ({ row }) => (
          <Button
            color="primary"
            size="sm"
            onClick={() => handleRowClickDetails(row.original)}
          >
            عرض التفاصيل
          </Button>
        ),
      },
     
    ],
    []
  );

  const Status = ({ value }) => {
    const statusMap = {
      paid: { color: 'success', icon: 'bx bx-check-double', text: 'تم الدفع' },
      pending: { color: 'warning', icon: 'bx bx-time-five', text: 'قيد الانتظار' },
      failed: { color: 'danger', icon: 'bx bx-block', text: 'فشل' },
    };
    const status = statusMap[value] || { color: 'secondary', icon: '', text: value || 'غير معروف' };
    return (
      <Badge color={status.color} className="font-size-12">
        <i className={`${status.icon} align-middle me-1`} />
        {status.text}
      </Badge>
    );
  };

  return (
    <div className="page-content" dir="rtl">
      <Row>
        <Col lg="12">
          <Card>
            <CardBody>
              <h4 className="card-title mb-4">التقارير المالية</h4>

                {/* Export dropdown
                <Dropdown isOpen={exportDropdownOpen} toggle={toggleExportDropdown}>
                  <DropdownToggle color="primary" caret>
                    تصدير البيانات
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={exportToExcel}>تصدير كملف Excel</DropdownItem>
                    <DropdownItem onClick={exportToPDF}>تصدير كملف PDF</DropdownItem>
                  </DropdownMenu>
                </Dropdown> */}
              
              {/* Quick filter buttons */}
              <div className="mb-3">
                
                <Button 
                  color={quickFilter === 'الكل' ? 'primary' : 'light'} 
                  className="me-2"
                  onClick={() => applyQuickFilter('الكل')}
                >
                  الكل
                </Button>
                <Button 
                  color={quickFilter === 'اليوم' ? 'primary' : 'light'} 
                  className="me-2"
                  onClick={() => applyQuickFilter('اليوم')}
                >
                  اليوم
                </Button>
                <Button 
                  color={quickFilter === 'الأسبوع' ? 'primary' : 'light'} 
                  className="me-2"
                  onClick={() => applyQuickFilter('الأسبوع')}
                >
                  الأسبوع
                </Button>
                <Button 
                  color={quickFilter === 'الشهر' ? 'primary' : 'light'} 
                  className="me-2"
                  onClick={() => applyQuickFilter('الشهر')}
                >
                  الشهر
                </Button>
                <Button 
                  color="secondary"
                  onClick={toggleDateRangeModal}
                >
                  نطاق مخصص
                </Button>
                {(startDate || endDate) && (
                  <Button color="link" onClick={clearFilters}>
                    مسح الفلتر
                  </Button>
                )}
                                <Button 
                
                  className="me-2 m-4"
                  color="success"
                  onClick={() => exportToExcel()}
                >
                 تصدير كملف Excel
                </Button>
              </div>
              
              {/* Active filter indicator */}
              {(startDate || endDate) && (
                <div className="mb-3">
                  <Badge color="info" className="me-2">
                    {startDate ? format(startDate, 'yyyy/MM/dd', { locale: ar }) : '...'} 
                    {' → '}
                    {endDate ? format(endDate, 'yyyy/MM/dd', { locale: ar }) : '...'}
                  </Badge>
                  <small className="text-muted">
                    {filteredTransactions.length} معاملة موجودة
                  </small>
                </div>
              )}

              <TableContainer
                columns={columns}
                data={filteredTransactions}
                customPageSize={10}
                className="custom-header-css"
              />
              

              
              <TransactionModal
                isOpen={isModalOpenDetails}
                toggleModal={toggleModalDetails}
                transaction={selectedTransactionDetails}
              />
              
              {/* Custom date range modal */}
              <Modal isOpen={isDateRangeModalOpen} toggle={toggleDateRangeModal} dir="rtl">
                <ModalHeader toggle={toggleDateRangeModal}>
                  اختر نطاق التاريخ
                </ModalHeader>
                <ModalBody>
                  <DatePicker
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => setDateRange(update)}
                    inline
                    locale={ar}
                    dateFormat="yyyy/MM/dd"
                    isClearable
                  />
                </ModalBody>
                <ModalFooter>
                  <Button color="primary" onClick={applyDateFilter}>
                    تطبيق
                  </Button>
                  <Button color="secondary" onClick={toggleDateRangeModal}>
                    إلغاء
                  </Button>
                </ModalFooter>
              </Modal>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default FinancialReport;